import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import {
  BookOpen,
  Film,
  GraduationCap,
  Sparkles,
  ArrowRight,
  ArrowLeft,
  Home,
} from "lucide-react";
import ThemeSwitch from "../components/ds/ThemeSwitch";

/**
 * StoryPage — "From Learning to Creation" narrative
 * ─────────────────────────────────────────────────────────────
 * Standalone editorial page. Not linked from navigation by
 * design — accessed via /story only.
 *
 * Visual language:
 *   • Soft Contrast Deep Navy tokens
 *   • Apple/Linear/Stripe typography rhythm
 *   • Three-platform table as instrument-grade panel
 *   • Calm staggered entrance (premium easing)
 */
const PLATFORMS = [
  {
    id: "design",
    icon: BookOpen,
    titleAr: "Trust Design",
    titleEn: "Trust Design",
    actionAr: "تُصدر كتاباً (رقمياً أو مطبوعاً)",
    actionEn: "Publish a book (digital or print)",
    contentAr: "رواية، قصة قصيرة، شعر، سيرة، دليل.",
    contentEn: "A novel, short story, poetry, memoir, or guide.",
  },
  {
    id: "tvd",
    icon: Film,
    titleAr: "Trust Visual Director",
    titleEn: "Trust Visual Director",
    actionAr: "تُنتج فيلماً قصيراً أو محتوى بصرياً",
    actionEn: "Produce a short film or visual content",
    contentAr: "قصيدة، خاطرة، ذكرى، فكرة فلسفية، أو مشهد من خيالك.",
    contentEn: "A poem, a memory, a thought, or a scene from your imagination.",
  },
  {
    id: "lwt",
    icon: GraduationCap,
    titleAr: "تبقى في Learn With Trust",
    titleEn: "Stay on Learn With Trust",
    actionAr: "تأخذ درساً جديداً",
    actionEn: "Take another lesson",
    contentAr: "لا يوجد ضغط للانتقال.",
    contentEn: "No pressure to move on.",
  },
];

export default function StoryPage() {
  const { language, localize } = useLanguage();
  const isAr = language === "ar";
  const Arrow = isAr ? ArrowLeft : ArrowRight;
  const HomeArrow = isAr ? ArrowRight : ArrowLeft;

  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);

  const fade = (i = 0) => ({
    opacity: mounted ? 1 : 0,
    transform: mounted ? "translateY(0)" : "translateY(8px)",
    transition: `opacity 700ms cubic-bezier(0.16,1,0.3,1) ${i * 90}ms, transform 700ms cubic-bezier(0.16,1,0.3,1) ${i * 90}ms`,
  });

  return (
    <div
      className="min-h-[100svh] bg-[var(--bg-primary)] text-[var(--text-primary)]"
      dir={isAr ? "rtl" : "ltr"}
      data-testid="story-page"
    >
      {/* Top utility bar */}
      <div className="border-b border-[var(--border-color)]">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
            data-testid="story-home-link"
          >
            <HomeArrow className="w-4 h-4" />
            <Home className="w-4 h-4 hidden sm:inline" />
            <span>{localize({ ar: "الرئيسية", en: "Home" })}</span>
          </Link>
          <ThemeSwitch size="sm" data-testid="story-theme-switch" />
        </div>
      </div>

      {/* Page body */}
      <main className="max-w-3xl mx-auto px-6 py-16 sm:py-24 space-y-16 sm:space-y-20">

        {/* Eyebrow */}
        <div style={fade(0)} className="flex justify-center">
          <span className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.22em] text-[var(--accent-primary)]">
            <Sparkles className="w-3.5 h-3.5" />
            {localize({ ar: "قصّتنا", en: "Our Story" })}
          </span>
        </div>

        {/* Hero */}
        <header style={fade(1)} className="text-center space-y-5">
          <h1
            className="text-3xl sm:text-5xl font-semibold tracking-tight leading-[1.08] text-[var(--text-primary)]"
            data-testid="story-hero-title"
          >
            {localize({
              ar: "من التعلّم إلى الإبداع — مسار حُرّ.",
              en: "From learning to creation — a free path.",
            })}
          </h1>
          <p className="text-base sm:text-lg text-[var(--text-secondary)] max-w-2xl mx-auto leading-relaxed">
            {localize({
              ar: "تتعلّم أولاً. ثم تبدع ما تشاء.",
              en: "Learn first. Then create whatever you want.",
            })}
          </p>
        </header>

        {/* Intro paragraph */}
        <section style={fade(2)} className="space-y-5">
          <p className="text-base sm:text-lg text-[var(--text-primary)] leading-relaxed">
            {localize({
              ar: (
                <>
                  كل رحلة في <strong className="text-[var(--accent-primary)]">Trust Ed Techno</strong> تبدأ من مكان واحد: <strong>التعلّم</strong>. تأخذ درساً على <strong>Learn With Trust</strong> مع معلّم موثوق، أو تتدرّب على مهارة جديدة بصحبة TED، مساعدنا الذكي.
                </>
              ),
              en: (
                <>
                  Every journey at <strong className="text-[var(--accent-primary)]">Trust Ed Techno</strong> starts in one place: <strong>learning</strong>. You take a lesson on <strong>Learn With Trust</strong> with a verified teacher, or build a new skill alongside TED, our AI assistant.
                </>
              ),
            })}
          </p>
          <p className="text-base sm:text-lg text-[var(--text-secondary)] leading-relaxed">
            {localize({
              ar: "وبعد أن ينتهي الدرس، يبدأ ما هو أجمل: مساحتك الإبداعية.",
              en: "And when the lesson ends, what's even better begins: your creative space.",
            })}
          </p>
        </section>

        {/* Divider */}
        <div style={fade(3)} className="h-px bg-[var(--border-color)]" aria-hidden />

        {/* Stage 1 */}
        <section style={fade(4)} className="space-y-4">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[var(--bg-secondary)] border border-[var(--border-color)] text-sm font-semibold text-[var(--accent-primary)]">1</span>
            <h2 className="text-xl sm:text-2xl font-semibold tracking-tight">
              {localize({
                ar: "تبدأ بالتعلّم",
                en: "Begin with learning",
              })}
            </h2>
          </div>
          <p className="text-base text-[var(--text-secondary)] leading-relaxed ms-11">
            {localize({
              ar: "درسٌ مباشر، أو محادثة مع TED، أو محتوى ذاتي تتقدّم فيه بسرعتك. هنا تبني الأساس: ثقة، أدوات، لغة، وأفكار.",
              en: "A live lesson, a chat with TED, or self-paced content you move through at your own speed. This is where the foundation is built: confidence, tools, language, and ideas.",
            })}
          </p>
        </section>

        {/* Stage 2 — the three doors */}
        <section style={fade(5)} className="space-y-6">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[var(--bg-secondary)] border border-[var(--border-color)] text-sm font-semibold text-[var(--accent-primary)]">2</span>
            <h2 className="text-xl sm:text-2xl font-semibold tracking-tight">
              {localize({
                ar: "ثم تنفتح الأبواب — وأنت تختار",
                en: "Then the doors open — and you choose",
              })}
            </h2>
          </div>
          <p className="text-base text-[var(--text-secondary)] leading-relaxed ms-11">
            {localize({
              ar: "عندما تنهي الدرس، تظهر أمامك ثلاثة مسارات إبداعية. كلها اختيارية، وكلها مفتوحة.",
              en: "When you finish a lesson, three creative paths appear. All optional. All open.",
            })}
          </p>

          {/* Platform panels */}
          <div className="grid grid-cols-1 gap-3 ms-11" data-testid="story-platforms">
            {PLATFORMS.map((p, idx) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.id}
                  className="bg-[var(--bg-secondary)] border border-[var(--border-color)] hover:border-[var(--accent-primary)] rounded-xl p-5 transition-colors"
                  data-testid={`story-platform-${p.id}`}
                  style={fade(6 + idx)}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[var(--bg-primary)] border border-[var(--border-color)] flex items-center justify-center shrink-0">
                      <Icon className="w-5 h-5 text-[var(--accent-primary)]" />
                    </div>
                    <div className="min-w-0 flex-1 space-y-1">
                      <div className="text-base font-semibold text-[var(--text-primary)]">
                        {localize({ ar: p.titleAr, en: p.titleEn })}
                      </div>
                      <div className="text-sm text-[var(--accent-primary)]">
                        {localize({ ar: p.actionAr, en: p.actionEn })}
                      </div>
                      <div className="text-sm text-[var(--text-secondary)]">
                        {localize({ ar: p.contentAr, en: p.contentEn })}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Inline example */}
          <div
            style={fade(9)}
            className="ms-11 px-5 py-4 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)]/60 text-sm text-[var(--text-secondary)] leading-relaxed italic"
            data-testid="story-example"
          >
            {localize({
              ar: "💡 تعلّمتَ الرياضيات هذا الأسبوع — وكتبتَ قصة عن جدّتك. تعلّمتَ الكتابة الإبداعية — وصنعتَ فيلماً عن السماء.",
              en: "💡 You learned math this week — and wrote a story about your grandmother. You learned creative writing — and made a film about the sky.",
            })}
          </div>
        </section>

        {/* Stage 3 */}
        <section style={fade(10)} className="space-y-4">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[var(--bg-secondary)] border border-[var(--border-color)] text-sm font-semibold text-[var(--accent-primary)]">3</span>
            <h2 className="text-xl sm:text-2xl font-semibold tracking-tight">
              {localize({
                ar: "أو لا شيء من ذلك — والاختيار خيارُك",
                en: "Or none of it — and the choice is yours",
              })}
            </h2>
          </div>
          <p className="text-base text-[var(--text-secondary)] leading-relaxed ms-11">
            {localize({
              ar: "يبقى المُتعلّم متعلّماً إن أراد، ويصبح كاتباً وصانع أفلام متى أراد.",
              en: "A learner stays a learner if they wish — and becomes a writer or a filmmaker whenever they choose.",
            })}
          </p>
        </section>

        {/* Divider */}
        <div style={fade(11)} className="h-px bg-[var(--border-color)]" aria-hidden />

        {/* Tagline */}
        <section style={fade(12)} className="text-center space-y-2">
          <p className="text-lg sm:text-xl font-medium text-[var(--text-primary)] italic leading-relaxed">
            {localize({
              ar: "درسٌ يُكمل اليوم. كتابٌ تكتبه غداً. فيلمٌ تصنعه بعد ذلك.",
              en: "A lesson today. A book tomorrow. A film after that.",
            })}
          </p>
        </section>

        {/* CTA cluster */}
        <section style={fade(13)} className="space-y-4">
          <Link
            to="/"
            className="group flex items-center justify-between gap-4 rounded-xl px-6 py-4 bg-[var(--accent-primary)] hover:bg-[var(--accent-primary-hover)] text-white transition-colors"
            data-testid="story-cta-primary"
          >
            <span className="text-base font-semibold">
              {localize({ ar: "ابدأ بدرسك الأول", en: "Start your first lesson" })}
            </span>
            <Arrow className="w-5 h-5 transition-transform group-hover:translate-x-0.5" />
          </Link>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Link
              to="/"
              className="group flex items-center justify-between gap-4 rounded-xl px-5 py-4 bg-[var(--bg-secondary)] border border-[var(--border-color)] hover:border-[var(--accent-primary)] text-[var(--text-primary)] transition-colors"
              data-testid="story-cta-design"
            >
              <span className="flex items-center gap-3">
                <BookOpen className="w-4 h-4 text-[var(--accent-primary)]" />
                <span className="text-sm font-semibold">
                  {localize({ ar: "اكتب كتابك", en: "Write your book" })}
                  <span className="ms-2 text-xs font-normal text-[var(--text-tertiary)]">
                    {localize({ ar: "(اختياري)", en: "(optional)" })}
                  </span>
                </span>
              </span>
              <Arrow className="w-4 h-4 text-[var(--text-tertiary)] group-hover:text-[var(--accent-primary)] transition-colors" />
            </Link>
            <Link
              to="/"
              className="group flex items-center justify-between gap-4 rounded-xl px-5 py-4 bg-[var(--bg-secondary)] border border-[var(--border-color)] hover:border-[var(--accent-primary)] text-[var(--text-primary)] transition-colors"
              data-testid="story-cta-tvd"
            >
              <span className="flex items-center gap-3">
                <Film className="w-4 h-4 text-[var(--accent-primary)]" />
                <span className="text-sm font-semibold">
                  {localize({ ar: "اصنع فيلمك", en: "Make your film" })}
                  <span className="ms-2 text-xs font-normal text-[var(--text-tertiary)]">
                    {localize({ ar: "(اختياري)", en: "(optional)" })}
                  </span>
                </span>
              </span>
              <Arrow className="w-4 h-4 text-[var(--text-tertiary)] group-hover:text-[var(--accent-primary)] transition-colors" />
            </Link>
          </div>
        </section>

        {/* Footer note */}
        <p style={fade(14)} className="text-center text-xs text-[var(--text-tertiary)] tracking-wide pt-6">
          {localize({
            ar: "© 2026 ترست اي دي تكنو للتطوير والابتكار ذ.م.م",
            en: "© 2026 Trust Ed Techno for Development and Innovation L.L.C.",
          })}
        </p>
      </main>
    </div>
  );
}
